import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { useSite } from "@/context/SiteContext";
import pitchAerial from "@/assets/pitch-aerial.jpg";
import teamPhoto from "@/assets/team.jpg";
import {
  Home as HomeIcon, Building2, Ruler, ArrowRight, ClipboardCheck, Lightbulb,
  Wrench, LifeBuoy, ShieldCheck, Users, BadgeCheck, Phone, Award,
} from "lucide-react";
import svcWorkshop from "@/assets/svc-team-3.png";
import domesticCardImg from "@/assets/luba-hero.webp";
import brandStiga from "@/assets/brand-stiga.png";
import brandMammotion from "@/assets/brand-mammotion.png";
import brandFjd from "@/assets/brand-fjdynamics.png";
import brandCastel from "@/assets/brand-castelgarden.png";
import brandAmbrogio from "@/assets/brand-ambrogio.png";

const ROUTES = [
  { to: "/commercial", icon: Building2, badge: "Commercial", title: "Commercial Mowing", copy: "Autonomous machinery for estates, parks, campuses and amenity grounds.", cta: "View Commercial Range" },
  { to: "/line-marking", icon: Ruler, badge: "Clubs & Sports", title: "Line Marking", copy: "Autonomous field marking for GAA, soccer, rugby and school pitches.", cta: "Explore Line Marking" },
  { to: "/domestic", icon: HomeIcon, badge: "Residential", title: "Domestic Lawncare", copy: "Quiet, precise robotic mowers for gardens — with expert advice, installation and support.", cta: "Filter Mowers" },
];

const PROCESS = [
  { icon: ClipboardCheck, title: "Assessment", copy: "We visit your site and understand your grounds, access and goals." },
  { icon: Lightbulb, title: "Recommendation", copy: "Plain-English advice on the right machine for your space and budget." },
  { icon: Wrench, title: "Installation", copy: "Professional setup, boundary configuration and a full demonstration." },
  { icon: LifeBuoy, title: "Support", copy: "Ongoing servicing, maintenance and expert help whenever you need it." },
];

const TRUST = [
  { icon: BadgeCheck, title: "Expert installation", copy: "Professional setup and configuration by our specialist team." },
  { icon: ShieldCheck, title: "Authorised brands", copy: "We work with leading robotic mower manufacturers." },
  { icon: LifeBuoy, title: "Ongoing support", copy: "Servicing and support to keep your mower performing." },
  { icon: Users, title: "Local expertise", copy: "Advice tailored to Irish gardens, clubs and commercial sites." },
];

export default function Home() {
  const site = useSite();
  const [featured, setFeatured] = useState([]);
  const [enq, setEnq] = useState({ open: false, product: null });

  useEffect(() => {
    api.get("/products", { params: { category: "domestic" } })
      .then((res) => {
        const luba3 = res.data.filter((p) => ["luba3-awd1500", "luba3-awd3000", "luba3-awd5000"].includes(p.slug));
        setFeatured(luba3.length ? luba3 : res.data.slice(0, 3));
      })
      .catch(() => {});
  }, []);

  const openEnquiry = (product) => setEnq({ open: true, product });

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-brand-ink text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ink via-brand-ink/95 to-brand-ink" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="inline-block font-mono-spec text-xs uppercase tracking-widest text-white">
              Irish specialist in automated turf care
            </span>
            <h1 className="mt-4 font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              A better way to care for your grounds.
            </h1>
            <p className="mt-6 max-w-xl text-lg sm:text-xl text-white/80 leading-relaxed">
              Robotic mowing and line-marking solutions for homes, clubs and commercial sites — with
              expert advice, installation and ongoing support.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-white hover:bg-white/90 text-brand-navy font-semibold text-base h-12 px-7" data-testid="hero-explore-button">
                <Link to="/domestic">Explore Our Solutions <ArrowRight className="ml-2 h-5 w-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base border-white/30 bg-white/5 text-white hover:bg-white hover:text-brand-ink" data-testid="hero-assessment-button">
                <Link to="/book-assessment">Book a Site Assessment</Link>
              </Button>
            </div>
          </div>
          <div className="relative">
            <img
              src={teamPhoto}
              alt="The Robotic Lawnmower Experts team with their branded vans and robotic mowers"
              className="w-full rounded-3xl shadow-2xl ring-1 ring-white/10 object-cover"
              data-testid="hero-team-image"
            />
          </div>
        </div>
      </section>

      {/* Solution routes */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Three ways we can help</h2>
          <p className="mt-4 text-lg text-muted-foreground">Choose the route that fits your grounds. Every path starts with expert advice.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {ROUTES.map((r) => (
            <Link key={r.to} to={r.to} className="group flex flex-col rounded-2xl border border-brand-navy/10 bg-white p-8 transition-transform duration-200 hover:-translate-y-1 hover:border-brand-navy/25" data-testid={`route-${r.to.replace("/", "")}`}>
              <span className="inline-flex w-fit items-center gap-2 text-[11px] font-mono-spec uppercase tracking-widest px-2 py-1 rounded-full bg-brand-navy/10 text-brand-navy">{r.badge}</span>
              <span className="mt-5 grid place-items-center h-12 w-12 rounded-xl bg-brand-navy text-white"><r.icon className="h-6 w-6" /></span>
              <h3 className="mt-5 font-display font-semibold text-xl text-brand-navy">{r.title}</h3>
              <p className="mt-2 text-muted-foreground flex-1">{r.copy}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-semibold text-brand-ink group-hover:text-brand-navy transition-colors">{r.cta} <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* Our Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="home-our-services">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Our Services</h2>
          <p className="mt-4 text-lg text-muted-foreground">Comprehensive robotic lawn mower solutions for every need.</p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {[
            { value: "1000+", label: "Installations" },
            { value: "10+", label: "Years Experience" },
            { value: "2000+", label: "Repairs" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-brand-navy">{s.value}</div>
              <div className="mt-1 font-display font-semibold text-brand-ink">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { to: "/domestic", img: domesticCardImg, icon: HomeIcon, title: "Domestic Robotic Mowers", copy: "Professional installation and setup for your home garden. Enjoy a perfectly maintained lawn without the effort." },
            { to: "/commercial", img: pitchAerial, icon: Award, title: "Commercial Robotic Mowers", copy: "Large-area solutions for hotels, business parks and public spaces. Reliable, efficient and professional." },
            { to: "/service", img: svcWorkshop, icon: Wrench, title: "Service & Repairs", copy: "Expert servicing, repairs and maintenance for all major robotic mower brands. Keep your mower in peak condition." },
          ].map((c) => (
            <Link key={c.to} to={c.to} className="group relative rounded-2xl overflow-hidden min-h-[18rem] flex flex-col justify-end p-7 transition-transform duration-200 hover:-translate-y-1" data-testid={`service-card-${c.to.replace("/", "")}`}>
              <img src={c.img} alt={c.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/70 to-brand-ink/30" />
              <div className="relative">
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-white/15 backdrop-blur-sm text-white ring-1 ring-white/25"><c.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-display font-bold text-xl text-white">{c.title}</h3>
                <p className="mt-2 text-sm text-white/85">{c.copy}</p>
                <span className="mt-4 inline-flex items-center gap-2 font-semibold text-white group-hover:gap-3 transition-all">Learn more <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {TRUST.map((t) => (
            <div key={t.title} className="flex flex-col">
              <t.icon className="h-8 w-8 text-white" />
              <h3 className="mt-4 font-display font-semibold text-lg text-white">{t.title}</h3>
              <p className="mt-2 text-sm text-white/75">{t.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Commercial feature */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="relative rounded-3xl overflow-hidden">
          <img src={pitchAerial} alt="Aerial view of a GAA pitch with fresh line markings" className="w-full h-80 lg:h-[26rem] object-cover" />
        </div>
        <div>
          <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Commercial & clubs</span>
          <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Large-area mowing and line marking</h2>
          <p className="mt-4 text-lg text-muted-foreground">
            From sports pitches to corporate campuses, we help clubs and organisations keep grounds
            in top condition with autonomous machinery, professional installation and priority support.
          </p>
          <ul className="mt-6 space-y-3">
            {["Site surveys and needs-based guidance","Autonomous mowing for large areas","Line marking for pitches and courts","Priority servicing and support"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-brand-ink"><span className="h-2 w-2 rounded-full bg-brand-navy" /> {f}</li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap gap-4">
            <Button asChild className="bg-brand-navy hover:bg-brand-navy/90 text-white"><Link to="/commercial">Commercial Mowing</Link></Button>
            <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/line-marking">Line Marking</Link></Button>
          </div>
        </div>
      </section>

      {/* Featured domestic range */}
      <section className="bg-white border-y border-brand-navy/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Featured robotic mowers</h2>
              <p className="mt-4 text-lg text-muted-foreground">A selection from our published domestic range. Expert installation and support included.</p>
            </div>
            <Button asChild variant="outline" className="border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/domestic">View full catalogue</Link></Button>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.slug} product={p} onEnquire={openEnquiry} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">How it works</h2>
          <p className="mt-4 text-lg text-muted-foreground">A straightforward path from first visit to a perfectly maintained lawn.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS.map((s, i) => (
            <div key={s.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="grid place-items-center h-10 w-10 rounded-lg bg-brand-navy/10 text-brand-navy"><s.icon className="h-5 w-5" /></span>
                <span className="font-mono-spec text-sm text-brand-navy">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Service + About intro */}
      <section className="bg-brand-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-3xl bg-brand-navy text-white p-8 md:p-10 flex flex-col">
            <Wrench className="h-9 w-9 text-white" />
            <h3 className="mt-5 font-display font-bold text-2xl">Service & Repairs</h3>
            <p className="mt-3 text-white/80">Thorough workshop servicing for all major robotic mower brands — strip down, clean, inspect, diagnose, rebuild and fully test.</p>
            <Button asChild className="mt-6 w-fit bg-white hover:bg-white/90 text-brand-navy font-semibold"><Link to="/service">Explore servicing</Link></Button>
          </div>
          <div className="rounded-3xl bg-white border border-brand-navy/10 p-8 md:p-10 flex flex-col">
            <Users className="h-9 w-9 text-brand-navy" />
            <h3 className="mt-5 font-display font-bold text-2xl text-brand-ink">Local specialists</h3>
            <p className="mt-3 text-muted-foreground">Founded by John Gorman, Robotic Lawnmower Experts has installed hundreds of robotic mowers and completed thousands of service and repair jobs across Ireland.</p>
            <Button asChild variant="outline" className="mt-6 w-fit border-brand-navy/30 text-brand-navy hover:bg-brand-navy hover:text-white"><Link to="/about">Our story</Link></Button>
          </div>
        </div>
      </section>

      {/* Brands we work with */}
      <section className="bg-white border-y border-brand-navy/10" data-testid="home-brands">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Brands We Work With</h2>
            <p className="mt-4 text-lg text-muted-foreground">Authorised partners of leading robotic mower manufacturers.</p>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 items-center">
            {[
              { src: brandStiga, name: "Stiga" },
              { src: brandMammotion, name: "Mammotion" },
              { src: brandFjd, name: "FJDynamics" },
              { src: brandCastel, name: "CastelGarden" },
              { src: brandAmbrogio, name: "Ambrogio Robot" },
            ].map((b) => (
              <div key={b.name} className="flex items-center justify-center rounded-2xl border border-brand-navy/10 bg-white p-6 h-28 transition-transform duration-200 hover:-translate-y-1" data-testid={`brand-${b.name.split(" ")[0].toLowerCase()}`}>
                <img src={b.src} alt={`${b.name} logo`} loading="lazy" className="max-h-14 w-auto object-contain grayscale opacity-80 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-brand-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl">Ready to transform your lawn care?</h2>
          <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto">Get in touch for expert advice and to arrange a site assessment.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="h-12 px-7 bg-white hover:bg-white/90 text-brand-navy font-semibold"><Link to="/book-assessment">Book a Site Assessment</Link></Button>
            <Button asChild size="lg" variant="outline" className="h-12 px-7 border-white/30 bg-white/5 text-white hover:bg-white hover:text-brand-ink"><a href={`tel:${site.phone?.replace(/\s/g, "")}`}><Phone className="mr-2 h-5 w-5" /> {site.phone}</a></Button>
          </div>
        </div>
      </section>

      <EnquiryDialog open={enq.open} onOpenChange={(o) => setEnq((e) => ({ ...e, open: o }))} type="domestic" product={enq.product} sourcePage="home" />
    </div>
  );
}
